import { useMemo } from 'react'

/**
 * Totals for the days currently in view on the chart — the range, or the zoom.
 *
 * Deliberately a single line rather than a row of tiles. The day pages size
 * themselves to the viewport so the message log never pushes the page into a
 * scroll, and a second row of tiles would eat the log's space. It also has to
 * read as visibly different from the selected-day tiles below it: those
 * describe one day, this describes a period, and conflating the two was the
 * problem this component exists to fix.
 *
 * Computed client-side from the per-day series the chart already fetched, so
 * zooming updates it without a request.
 */

export interface PeriodRow {
  day: string
  missedMessages: number
  receivedReadings: number
  /** Null on a day with no usable interval estimate (e.g. reconfigured). */
  expectedReadings: number | null
}

interface PeriodSummaryProps {
  rows: PeriodRow[]
  from: string
  to: string
  zoomed: boolean
  onSelectDay: (day: string) => void
  /**
   * Brush the chart: called with the days a stat refers to while it is hovered
   * or focused, and with null when it is left. Lets "Days with gaps 4 of 17"
   * point at the four bars instead of leaving the reader to count them.
   */
  onHighlight?: (days: string[] | null) => void
}

export default function PeriodSummary({ rows, from, to, zoomed, onSelectDay, onHighlight }: PeriodSummaryProps) {
  const stats = useMemo(() => {
    const inView = rows.filter((r) => r.day >= from && r.day <= to)

    let missed = 0
    let daysWithMisses = 0
    const gapDays: string[] = []
    let worst: PeriodRow | null = null
    // Readings are only compared on days that HAVE an estimate. Summing every
    // day's received against only some days' expected would overstate
    // delivery on any period containing a reconfiguration.
    let received = 0
    let expected = 0
    let estimatedDays = 0

    for (const r of inView) {
      missed += r.missedMessages
      if (r.missedMessages > 0) {
        daysWithMisses += 1
        gapDays.push(r.day)
      }
      if (!worst || r.missedMessages > worst.missedMessages) worst = r
      if (r.expectedReadings !== null) {
        received += r.receivedReadings
        expected += r.expectedReadings
        estimatedDays += 1
      }
    }

    const dayCount =
      Math.round((new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()) / 86_400_000) + 1

    return {
      missed,
      daysWithMisses,
      gapDays,
      dayCount,
      worst: worst && worst.missedMessages > 0 ? worst : null,
      received,
      expected,
      estimatedDays,
      pct: expected > 0 ? Math.round((received / expected) * 1000) / 10 : null,
    }
  }, [rows, from, to])

  /** Hover and keyboard focus both brush; leaving either clears it. */
  const brush = (days: string[]) =>
    onHighlight
      ? {
          onMouseEnter: () => onHighlight(days),
          onMouseLeave: () => onHighlight(null),
          onFocus: () => onHighlight(days),
          onBlur: () => onHighlight(null),
        }
      : {}

  return (
    <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs">
      <span className="font-medium text-gray-700">
        {zoomed ? 'Zoomed period' : 'Period'}
        <span className="ml-1.5 font-normal tabular-nums text-gray-400">
          {from} → {to} · {stats.dayCount} days
        </span>
      </span>

      <span className="text-gray-500">
        Missed{' '}
        <span className={`tabular-nums font-semibold ${stats.missed > 0 ? 'text-red-600' : 'text-gray-700'}`}>
          {stats.missed.toLocaleString()}
        </span>
      </span>

      <span
        className={`text-gray-500 ${
          onHighlight && stats.daysWithMisses > 0 ? 'cursor-help rounded px-1 -mx-1 hover:bg-red-50' : ''
        }`}
        tabIndex={onHighlight && stats.daysWithMisses > 0 ? 0 : undefined}
        {...(stats.daysWithMisses > 0 ? brush(stats.gapDays) : {})}
      >
        Days with gaps{' '}
        <span className="tabular-nums font-semibold text-gray-700">
          {stats.daysWithMisses} of {stats.dayCount}
        </span>
      </span>

      {stats.worst && (
        <span className="text-gray-500">
          Worst day{' '}
          <button
            type="button"
            {...brush([stats.worst.day])}
            onClick={() => onSelectDay(stats.worst!.day)}
            className="tabular-nums font-semibold text-blue-600 hover:underline"
            title="Open this day"
          >
            {stats.worst.day}
          </button>{' '}
          <span className="tabular-nums text-red-600">({stats.worst.missedMessages.toLocaleString()})</span>
        </span>
      )}

      <span
        className="text-gray-500"
        title={
          stats.estimatedDays < stats.dayCount
            ? `Compared over the ${stats.estimatedDays} day(s) that have an interval estimate`
            : undefined
        }
      >
        Readings{' '}
        <span className="tabular-nums font-semibold text-gray-700">
          {stats.expected > 0 ? `${stats.received.toLocaleString()} of ${stats.expected.toLocaleString()}` : '—'}
        </span>
        {stats.pct !== null && <span className="ml-1 tabular-nums text-gray-400">({stats.pct}%)</span>}
      </span>
    </div>
  )
}
